import { toast } from "@assetwatch/ui";
import { useMutation } from "@tanstack/react-query";
import type { RefObject } from "react";
import type { PendingProcedureSave } from "../components/work-orders/ProcedureExecutionLayout";
import type { WorkOrderId } from "../lib/workIds";
import { useProcedureAttachmentQuery } from "../queries/useProcedureAttachment";
import { useProcedureExecutionQuery } from "../queries/useProcedureExecution";

/** Saves pending answers and checks required steps before opening closeout. */
export function usePrepareWorkOrderCompletionMutation(
  workOrderId: WorkOrderId,
  pendingSaveRef: RefObject<PendingProcedureSave | null>,
) {
  const attachmentQuery = useProcedureAttachmentQuery(workOrderId);
  const executionQuery = useProcedureExecutionQuery(workOrderId, false);

  return useMutation({
    mutationKey: ["prepare-work-order-completion", workOrderId],
    mutationFn: async () => {
      await pendingSaveRef.current?.();
      // Refetch after saving: a cached snapshot may predate the latest answer
      // or a Procedure replacement by another user.
      const attachment = await attachmentQuery.refetch({ throwOnError: true });
      if (attachment.data === null) return;
      if (!attachment.data)
        throw new Error("Unable to check the attached Procedure.");
      const { data: execution } = await executionQuery.refetch({
        throwOnError: true,
      });
      if (!execution)
        throw new Error("Unable to check the attached Procedure.");
      if (
        execution.procedureId !== attachment.data.procedureId ||
        execution.version !== attachment.data.procedureVersion
      )
        throw new Error("The attached Procedure changed. Try again.");

      const missingSteps = execution.procedure.items.filter((item) => {
        if (!item.required) return false;
        const response = execution.responses.find(
          (answer) => answer.itemId === item.id,
        )?.value;
        if (!response || response.type !== item.type) return true;
        switch (response.type) {
          case "CHECKBOX":
            return !response.value;
          case "PHOTO":
            return response.fileIds.length === 0;
          case "NUMERIC":
            return !Number.isFinite(response.value);
          default:
            return response.value.trim().length === 0;
        }
      });
      if (missingSteps.length) {
        throw new Error(
          `Complete the required steps in the attached Procedure before marking this Work Order Done: ${missingSteps.map((item) => item.label).join(", ")}.`,
        );
      }
    },
    onError: (error) => {
      toast.error(error.message || "Unable to check the attached Procedure.", {
        icon: true,
      });
    },
  });
}
